import React from "react";
import { Link } from "react-router-dom";
import {
  Hexagon, FileText, Tags, FileCheck, BellRing,
  Database, FolderCog, ArrowRight,
} from "lucide-react";
import { getServices } from "@/lib/siteContent";
import { useLanguage } from "@/context/LanguageContext";

const ICONS = { Hexagon, FileText, Tags, FileCheck, BellRing, Database, FolderCog };

const Services = () => {
  const { language, t } = useLanguage();
  const SERVICES = getServices(language);

  const PROCESS_STEPS = [
    { step: "01", title: t("services.process.step1.title"), desc: t("services.process.step1.desc") },
    { step: "02", title: t("services.process.step2.title"), desc: t("services.process.step2.desc") },
    { step: "03", title: t("services.process.step3.title"), desc: t("services.process.step3.desc") },
    { step: "04", title: t("services.process.step4.title"), desc: t("services.process.step4.desc") },
  ];

  return (
    <div data-testid="services-page">
      <section className="relative pt-24 pb-16 lg:pt-32 lg:pb-20 bg-white">
        <div className="absolute inset-0 hex-pattern opacity-100 pointer-events-none" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-7">
            <div className="text-xs uppercase tracking-[0.22em] text-teal-700 mb-4">{t("services.kicker")}</div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-slate-900 tracking-tight">
              {t("services.titlePrefix")} <em className="not-italic text-teal-700">{t("services.titleEm")}</em>.
            </h1>
          </div>
          <div className="lg:col-span-5">
            <p className="text-slate-600 leading-relaxed">
              {t("services.lead")}
            </p>
          </div>
        </div>
      </section>

      {/* Services list — image + content alternating */}
      <section className="pb-20 lg:pb-28 bg-white" data-testid="services-list">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 space-y-8">
          {SERVICES.map((s, idx) => {
            const Icon = ICONS[s.icon] || Hexagon;
            const reverse = idx % 2 === 1;
            return (
              <Link
                to={`/hizmetler/${s.slug}`}
                key={s.slug}
                className="group grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10 rounded-2xl border border-slate-200 bg-white overflow-hidden card-hover"
                data-testid={`service-row-${idx}`}
              >
                <div className={`lg:col-span-5 ${reverse ? "lg:order-2" : ""}`}>
                  <div className="relative h-full min-h-[260px] overflow-hidden">
                    <img
                      src={s.image}
                      alt={s.title}
                      loading="lazy"
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute top-4 left-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/90 backdrop-blur text-teal-800 text-xs font-medium border border-teal-100">
                      <Icon size={13} /> 0{idx + 1}
                    </div>
                  </div>
                </div>

                <div className="lg:col-span-7 p-6 lg:p-10 flex flex-col justify-center">
                  <div className="text-xs uppercase tracking-[0.2em] text-teal-700 mb-3">
                    {s.tagline}
                  </div>
                  <h2 className="font-display text-2xl lg:text-3xl text-slate-900 tracking-tight">
                    {s.title}
                  </h2>
                  <p className="mt-3 text-slate-600 leading-relaxed">{s.short}</p>
                  <p className="mt-2 text-slate-600 leading-relaxed">{s.description}</p>

                  <div className="mt-5 flex flex-wrap gap-2">
                    {s.highlights.slice(0, 4).map((h) => (
                      <span
                        key={h}
                        className="inline-flex items-center px-3 py-1 rounded-full bg-teal-50 text-teal-800 text-xs"
                      >
                        {h}
                      </span>
                    ))}
                  </div>

                  <div
                    className="mt-6 inline-flex items-center gap-1.5 text-sm font-medium text-teal-700 group-hover:text-teal-800"
                    data-testid={`service-cta-${idx}`}
                  >
                    {t("services.detailLink")} <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" />
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* Process strip */}
      <section className="py-20 lg:py-28 bg-[#F0FDFA]" data-testid="services-process">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-xs uppercase tracking-[0.22em] text-teal-700 mb-3">{t("services.processKicker")}</div>
          <h2 className="font-display text-3xl lg:text-5xl text-slate-900 tracking-tight max-w-3xl">
            {t("services.processTitle")}
          </h2>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-4 gap-6">
            {PROCESS_STEPS.map((p, idx) => (
              <div
                key={p.step}
                className="p-6 rounded-xl bg-white border border-slate-200 card-hover"
                data-testid={`process-step-${idx}`}
              >
                <div className="font-display text-3xl text-teal-700">{p.step}</div>
                <div className="mt-3 font-medium text-slate-900">{p.title}</div>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
